import { createRootRoute, createRoute, createRouter, redirect } from '@tanstack/react-router';
import type { AssetKind } from '@shared/types';
import { AppShell } from './pages/AppShell';
import { LoginPage } from './pages/LoginPage';
import { MagicPage } from './pages/MagicPage';
import { BrowsePage } from './pages/BrowsePage';
import { AssetDetailPage } from './pages/AssetDetailPage';
import { SearchPage } from './pages/SearchPage';
import { ReviewPage } from './pages/ReviewPage';
import { PromptPage } from './pages/PromptPage';
import { AccountPage } from './pages/AccountPage';
import { UsersPage } from './pages/UsersPage';
import { SettingsPage } from './pages/SettingsPage';
import { api } from './lib/api';
import { keys } from './lib/queries';
import { queryClient } from './lib/queryClient';

const rootRoute = createRootRoute({
  notFoundComponent: () => (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-2 surface-page text-center">
      <p className="text-3xl font-semibold text-strong">404</p>
      <a href="/" className="text-sm text-brand-500 hover:underline">Back to the library</a>
    </div>
  ),
});

async function requireAuth() {
  try {
    return { user: await queryClient.ensureQueryData({ queryKey: keys.me, queryFn: () => api.me().then((r) => r.user), staleTime: 15 * 60_000 }) };
  } catch {
    throw redirect({ to: '/login' });
  }
}

// Admin-only guard for the Manage pages.
async function requireAdmin() {
  const { user } = await requireAuth();
  if (!user?.isAdmin) throw redirect({ to: '/' });
}

type BrowseSearch = {
  kind?: AssetKind;
  sort?: string;
  order?: 'asc' | 'desc';
  untagged?: boolean;
  favourites?: boolean;
};
const parseBrowseSearch = (s: Record<string, unknown>): BrowseSearch => ({
  kind: s.kind as AssetKind | undefined,
  sort: typeof s.sort === 'string' ? s.sort : undefined,
  order: s.order === 'desc' ? 'desc' : s.order === 'asc' ? 'asc' : undefined,
  untagged: s.untagged === true || s.untagged === 'true' ? true : undefined,
  favourites: s.favourites === true || s.favourites === 'true' ? true : undefined,
});

type SearchSearch = { q?: string; brand?: string; imageType?: string };
const parseSearchSearch = (s: Record<string, unknown>): SearchSearch => ({
  q: typeof s.q === 'string' ? s.q : undefined,
  brand: typeof s.brand === 'string' ? s.brand : undefined,
  imageType: typeof s.imageType === 'string' ? s.imageType : undefined,
});

const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: '/login', component: LoginPage });
const magicRoute = createRoute({ getParentRoute: () => rootRoute, path: '/auth/magic', component: MagicPage });

const shellRoute = createRoute({ getParentRoute: () => rootRoute, id: 'shell', beforeLoad: requireAuth, component: AppShell });

const indexRoute = createRoute({ getParentRoute: () => shellRoute, path: '/', component: BrowsePage, validateSearch: parseBrowseSearch });
const folderRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/folders/$folderId',
  parseParams: ({ folderId }) => ({ folderId: Number(folderId) }),
  validateSearch: parseBrowseSearch,
  component: BrowsePage,
});
const assetRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/assets/$assetId',
  parseParams: ({ assetId }) => ({ assetId: Number(assetId) }),
  component: AssetDetailPage,
});
const searchRoute = createRoute({ getParentRoute: () => shellRoute, path: '/search', component: SearchPage, validateSearch: parseSearchSearch });
const reviewRoute = createRoute({ getParentRoute: () => shellRoute, path: '/review', beforeLoad: requireAdmin, component: ReviewPage });
const promptRoute = createRoute({ getParentRoute: () => shellRoute, path: '/prompt', beforeLoad: requireAdmin, component: PromptPage });
const usersRoute = createRoute({ getParentRoute: () => shellRoute, path: '/users', beforeLoad: requireAdmin, component: UsersPage });
const settingsRoute = createRoute({ getParentRoute: () => shellRoute, path: '/settings', beforeLoad: requireAdmin, component: SettingsPage });
const accountRoute = createRoute({ getParentRoute: () => shellRoute, path: '/account', component: AccountPage });

const routeTree = rootRoute.addChildren([
  loginRoute,
  magicRoute,
  shellRoute.addChildren([indexRoute, folderRoute, assetRoute, searchRoute, reviewRoute, promptRoute, usersRoute, settingsRoute, accountRoute]),
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
